import { useParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/base/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { comicId } = useParams<{ comicId: string }>();
  const comic = comics.find((c) => c.id === Number(comicId));

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">该漫画可能已被移除或链接错误</p>
            <Link to="/" className="inline-flex items-center gap-1 text-sm bg-primary-500 text-white px-5 py-2.5 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const chapters = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    number: comic.chapters - i,
    title: `第${comic.chapters - i}话${i === 0 ? ' - 最新' : ''}`,
    date: new Date(comic.updatedAt).getTime() - i * 7 * 24 * 3600 * 1000,
  }));

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: `${comic.categoryName}漫画`, href: `/category/${comic.category}` },
    { label: comic.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫在线免费阅读`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2024-01-15',
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '尾巴漫画网',
      url: 'https://www.weibamanhua.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.weibamanhua.com/comic/${comic.id}`,
    },
  };

  return (
    <>
      <SEOMeta
        title={`${comic.title}_${comic.title}韩漫在线免费阅读`}
        description={`${comic.description} 尾巴漫画网提供${comic.title}高清无删减韩漫在线免费阅读，下拉式设计沉浸体验。`}
        keywords={`${comic.title},${comic.title}韩漫,${comic.title}在线阅读,${comic.categoryName}韩漫,尾巴漫画网,韩漫在线免费阅读`}
        canonicalUrl={`https://www.weibamanhua.com/comic/${comic.id}`}
        ogType="article"
        schema={articleSchema}
      />
      <ScrollToTop />
      <Navbar />

      <main>
        <article className="bg-background-50">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb items={breadcrumbItems} />

            <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
              <div className="lg:w-[320px] flex-shrink-0">
                <div className="sticky top-24">
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-background-100">
                    <img
                      src={comic.cover}
                      alt={`${comic.title} - 韩国漫画封面`}
                      title={`${comic.title} - 韩漫在线免费阅读`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-foreground-100 text-foreground-600'
                  }`}>
                    {comic.status}
                  </span>
                  <Link
                    to={`/category/${comic.category}`}
                    className="text-xs bg-secondary-100 text-secondary-700 px-2.5 py-1 rounded-full hover:bg-secondary-200 transition-colors whitespace-nowrap"
                  >
                    {comic.categoryName}
                  </Link>
                </div>

                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500 mb-4">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line text-foreground-400"></i>
                    作者：{comic.author}
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-paint-brush-line text-foreground-400"></i>
                    画师：{comic.artist}
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span className="flex items-center gap-1 text-accent-500 font-medium">
                    <i className="ri-star-fill"></i>
                    {comic.rating}分
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line text-foreground-400"></i>
                    {comic.views}
                  </span>
                </div>

                {comic.tags && (
                  <div className="flex flex-wrap gap-2 mb-5">
                    {comic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs bg-background-100 border border-background-200/70 text-foreground-600 px-3 py-1 rounded-full whitespace-nowrap"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}

                <div className="bg-background-100 rounded-2xl p-5 mb-6">
                  <h2 className="text-sm font-semibold text-foreground-800 mb-2 flex items-center gap-1.5">
                    <i className="ri-file-text-line text-primary-500"></i>
                    作品简介
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-semibold text-foreground-800 flex items-center gap-1.5">
                      <i className="ri-list-check text-primary-500"></i>
                      章节列表
                      <span className="text-xs text-foreground-500 font-normal ml-1">
                        (共{comic.chapters}话)
                      </span>
                    </h2>
                  </div>

                  <div className="bg-background-100 rounded-2xl overflow-hidden">
                    <div className="divide-y divide-background-200/70">
                      {chapters.map((ch, index) => (
                        <div
                          key={ch.number}
                          className="flex items-center justify-between px-5 py-3.5 hover:bg-background-200/40 transition-colors duration-200"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-foreground-400 font-mono w-12 whitespace-nowrap">
                              第{ch.number}话
                            </span>
                            <span className="text-sm text-foreground-700">
                              {ch.title}
                            </span>
                            {index === 0 && (
                              <span className="text-[10px] bg-primary-100 text-primary-600 px-1.5 py-0.5 rounded whitespace-nowrap">
                                NEW
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-foreground-400 whitespace-nowrap">
                              {new Date(ch.date).toISOString().slice(0, 10)}
                            </span>
                            <span className="cursor-pointer text-xs bg-primary-500 text-white px-3 py-1.5 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap flex items-center gap-1">
                              <i className="ri-play-line text-[10px]"></i>
                              阅读
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {comic.chapters > 20 && (
                      <div className="px-5 py-3 text-center border-t border-background-200/70">
                        <span className="text-xs text-foreground-500">
                          仅显示最近20话，更多章节敬请期待
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedComics.length > 0 && (
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100" aria-labelledby="related-heading">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <h2 id="related-heading" className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                  更多{comic.categoryName}漫画推荐
                </h2>
                <Link
                  to={`/category/${comic.category}`}
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 whitespace-nowrap"
                >
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
                {relatedComics.map((rc) => (
                  <ComicCard key={rc.id} comic={rc} size="medium" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}